import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Channel } from 'types/channel';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import StarDisplayAtom from '../../atoms/marketplace/StarDisplayAtom';
import {
  SubscribeButtonAtom,
  SubscribeButtonState,
} from '../../atoms/marketplace/SubscribeButtonAtom';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './FeaturedHeroCarouselMolecule.scss';

/**
 * Channel shape consumed by the hero. Extends `Channel` with the optional
 * marketplace-listing fields the hero surfaces (mandatory flag, metric
 * counts). Callers typically pass `ListedChannel` rows straight through.
 */
export type FeaturedHeroChannel = Channel & {
  is_mandatory?: boolean;
  subscriber_count?: number;
  item_count?: number;
};

export interface FeaturedHeroCarouselMoleculeLabels {
  featured: string;
  /** Badge rendered next to `featured` when `channel.is_mandatory` is true. */
  required: string;
  prev: string;
  next: string;
  goToSlide: (index: number) => string;
  /** Caller-formatted "N,NNN subs · M items" string. Molecule stays `t()`-free. */
  subsItemsLabel: (subs: number, items: number) => string;
}

export interface FeaturedHeroCarouselMoleculeProps {
  channels: FeaturedHeroChannel[];
  intervalMs?: number;
  /** Fired when the user clicks the headline to open the channel detail view. */
  onSelect: (channelId: string) => void;
  /** Fired when the state-aware subscribe button is clicked. */
  onSubscribeClick: (channelId: string) => void;
  labels: FeaturedHeroCarouselMoleculeLabels;
  /** Per-channel subscribe-button state, keyed by channel id. */
  subscriptionStates?: Record<string, SubscribeButtonState>;
  /** Per-channel loading flag for the subscribe button. */
  subscriptionLoading?: Record<string, boolean>;
  /** Pre-computed rating averages per channel id. */
  ratingAverages?: Record<string, number>;
  /** Pre-translated rating-count label per slide. */
  ratingCountLabels?: Record<string, string>;
  /**
   * Override the gradient/treatment used by the hero. Defaults to deriving
   * from the active channel's scope; the marketplace landing page passes
   * `'center'` to keep the hero on the Center navy gradient regardless of
   * which channel happens to be featured (matches the design's signature
   * marketplace hero).
   */
  variant?: 'center' | 'organization' | 'metasphere';
  className?: string;
}

const DEFAULT_INTERVAL_MS = 6000;

const FeaturedHeroCarouselMolecule: React.FC<FeaturedHeroCarouselMoleculeProps> = ({
  channels,
  intervalMs = DEFAULT_INTERVAL_MS,
  onSelect,
  onSubscribeClick,
  labels,
  subscriptionStates,
  subscriptionLoading,
  ratingAverages,
  ratingCountLabels,
  variant,
  className = '',
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const rootRef = useRef<HTMLElement | null>(null);

  const total = channels.length;
  const hasMultiple = total > 1;

  useEffect(() => {
    if (activeIndex >= total && total > 0) {
      setActiveIndex(0);
    }
  }, [activeIndex, total]);

  useEffect(() => {
    if (!hasMultiple || paused) {
      return undefined;
    }
    const id = globalThis.setInterval(() => {
      setActiveIndex((current) => (current + 1) % total);
    }, intervalMs);
    return () => {
      globalThis.clearInterval(id);
    };
  }, [hasMultiple, paused, intervalMs, total]);

  const goTo = useCallback(
    (index: number) => {
      if (total === 0) return;
      const normalized = ((index % total) + total) % total;
      setActiveIndex(normalized);
    },
    [total],
  );

  const handlePrev = useCallback(() => {
    goTo(activeIndex - 1);
  }, [activeIndex, goTo]);

  const handleNext = useCallback(() => {
    goTo(activeIndex + 1);
  }, [activeIndex, goTo]);

  if (total === 0) {
    return null;
  }

  const activeChannel = channels[Math.min(activeIndex, total - 1)];
  const displayName = getLocalizedText(activeChannel.name);
  const displayDescription = getLocalizedText(activeChannel.description);
  const ratingAverage = ratingAverages?.[activeChannel.id];
  const ratingCountLabel = ratingCountLabels?.[activeChannel.id];
  const subscribeState: SubscribeButtonState =
    subscriptionStates?.[activeChannel.id] ?? 'not_subscribed';
  const isSubscribeLoading = !!subscriptionLoading?.[activeChannel.id];
  const subsCount = activeChannel.subscriber_count;
  const itemsCount = activeChannel.item_count;
  const hasSubsItems =
    typeof subsCount === 'number' && typeof itemsCount === 'number';

  const classes = [
    'marketplace-featured-hero',
    `marketplace-featured-hero--${variant ?? activeChannel.scope}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleTitleClick = () => {
    onSelect(activeChannel.id);
  };

  const handleSubscribe = () => {
    onSubscribeClick(activeChannel.id);
  };

  return (
    <section
      ref={rootRef}
      className={classes}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="marketplace-featured-hero__stage" aria-live="polite">
        <div className="marketplace-featured-hero__body">
          <div className="marketplace-featured-hero__badges">
            <StatusBadgeAtom
              tone="warn"
              icon="pi pi-star-fill"
              label={labels.featured}
              size="medium"
            />
            {activeChannel.is_mandatory && (
              <StatusBadgeAtom
                tone="info"
                icon="pi pi-lock"
                label={labels.required}
                size="medium"
              />
            )}
          </div>

          <button
            type="button"
            className="marketplace-featured-hero__title"
            onClick={handleTitleClick}
          >
            {displayName}
          </button>

          {displayDescription && (
            <p className="marketplace-featured-hero__description">
              {displayDescription}
            </p>
          )}

          <div className="marketplace-featured-hero__actions">
            <SubscribeButtonAtom
              state={subscribeState}
              loading={isSubscribeLoading}
              onClick={handleSubscribe}
              className="marketplace-featured-hero__subscribe"
            />

            {typeof ratingAverage === 'number' && ratingAverage > 0 && (
              <div className="marketplace-featured-hero__rating">
                <StarDisplayAtom
                  rating={ratingAverage}
                  size="small"
                  variant="light"
                />
                {ratingCountLabel && (
                  <span className="marketplace-featured-hero__rating-count">
                    {ratingCountLabel}
                  </span>
                )}
              </div>
            )}

            {hasSubsItems && (
              <div className="marketplace-featured-hero__subs-items">
                <i
                  className="pi pi-users marketplace-featured-hero__subs-icon"
                  aria-hidden="true"
                />
                <span>{labels.subsItemsLabel(subsCount, itemsCount)}</span>
              </div>
            )}
          </div>
        </div>

        <aside
          className="marketplace-featured-hero__ornament"
          aria-hidden="true"
        >
          <div className="marketplace-featured-hero__ornament-ring marketplace-featured-hero__ornament-ring--outer" />
          <div className="marketplace-featured-hero__ornament-ring marketplace-featured-hero__ornament-ring--inner" />
          <div className="marketplace-featured-hero__ornament-coin">
            <ScopeCoinAtom
              scope={activeChannel.scope}
              glyph={activeChannel.glyph ?? null}
              channelName={displayName}
              size="lg"
            />
          </div>
          <div className="marketplace-featured-hero__ornament-tile marketplace-featured-hero__ornament-tile--metasphere">
            <i className="pi pi-globe" aria-hidden="true" />
          </div>
          <div className="marketplace-featured-hero__ornament-tile marketplace-featured-hero__ornament-tile--organization">
            <i className="pi pi-building" aria-hidden="true" />
          </div>
          <div className="marketplace-featured-hero__ornament-tile marketplace-featured-hero__ornament-tile--center">
            <i className="pi pi-bullseye" aria-hidden="true" />
          </div>
        </aside>
      </div>

      {hasMultiple && (
        <div className="marketplace-featured-hero__controls">
          <button
            type="button"
            className="marketplace-featured-hero__nav marketplace-featured-hero__nav--prev"
            aria-label={labels.prev}
            onClick={handlePrev}
          >
            <i className="pi pi-chevron-left" aria-hidden="true" />
          </button>
          <div className="marketplace-featured-hero__dots" role="tablist">
            {channels.map((channel, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={channel.id}
                  type="button"
                  role="tab"
                  className={
                    isActive
                      ? 'marketplace-featured-hero__dot marketplace-featured-hero__dot--active'
                      : 'marketplace-featured-hero__dot'
                  }
                  aria-label={labels.goToSlide(index)}
                  aria-current={isActive ? 'true' : undefined}
                  aria-selected={isActive}
                  onClick={() => goTo(index)}
                />
              );
            })}
          </div>
          <button
            type="button"
            className="marketplace-featured-hero__nav marketplace-featured-hero__nav--next"
            aria-label={labels.next}
            onClick={handleNext}
          >
            <i className="pi pi-chevron-right" aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
};

FeaturedHeroCarouselMolecule.displayName = 'FeaturedHeroCarouselMolecule';

export default FeaturedHeroCarouselMolecule;
